import z from "zod";

export const STORAGE_KEY = "reading-bee:v1";
export const INSTRUCTOR_UNLOCKED_KEY = "reading-bee:instructor-unlocked";
export const APP_VERSION = 4;
export const MIN_LEVEL = 1;
export const MAX_LEVEL = 100;
/** Last level that introduces isolated letters or letter combinations. */
export const LETTERS_MAX_LEVEL = 10;
/** Levels 1-7 only advance after a 5-right streak on every introduced sound. */
export const SOUND_MASTERY_MAX_LEVEL = 7;
/** Simple words start mixing in at this level. */
export const WORDS_INTRO_LEVEL = 8;
/** Letter prompts stay as common as new content through this level, then fade. */
export const LETTER_FADE_START_LEVEL = 10;
/** Letter and letter-combination prompts stop appearing at this level. */
export const LETTER_GONE_LEVEL = 14;
export const PASSCODE_LENGTH = 4;

export const ReadingBand = z.enum(["letters", "words", "phrases", "sentences", "books"]);
export type ReadingBand = z.infer<typeof ReadingBand>;

export const ReadingKind = z.enum(["letter", "word", "phrase", "sentence", "book"]);
export type ReadingKind = z.infer<typeof ReadingKind>;

export const ResultKind = z.enum(["right", "wrong", "skip", "wayTooEasy"]);
export type ResultKind = z.infer<typeof ResultKind>;

export const Subject = z.enum(["reading", "math", "spelling"]);
export type Subject = z.infer<typeof Subject>;

export const SubjectProgress = z.object({
  level: z.number().int().min(MIN_LEVEL).max(MAX_LEVEL),
  boostLevel: z.number().int().min(MIN_LEVEL).max(MAX_LEVEL),
  correctStreak: z.number().int().nonnegative(),
  wrongStreak: z.number().int().nonnegative(),
  maxCorrectStreak: z.number().int().nonnegative(),
  correctAnswers: z.number().int().nonnegative(),
  itemCorrect: z.record(z.string(), z.number().int().nonnegative()).default({}),
  mathSeen: z.record(z.string(), z.number().int().nonnegative()).default({}),
  mathWrong: z.record(z.string(), z.number().int().nonnegative()).default({}),
  mathHintHidden: z.record(z.string(), z.number().nonnegative()).default({}),
  mathReviewCount: z.number().int().nonnegative().default(0),
  mathReviewInterval: z.number().int().min(2).max(4).default(3),
  currentTextId: z.string().nullable().default(null),
  levelEpoch: z.number().int().nonnegative().optional(),
  events: z.array(
    z.object({
      id: z.string().optional(),
      result: ResultKind,
      at: z.string(),
      itemId: z.string().optional(),
    }),
  ),
});
export type SubjectProgress = z.infer<typeof SubjectProgress>;

const emptySubjectProgress = {
  level: MIN_LEVEL,
  boostLevel: MIN_LEVEL,
  correctStreak: 0,
  wrongStreak: 0,
  maxCorrectStreak: 0,
  correctAnswers: 0,
  itemCorrect: {},
  mathSeen: {},
  mathWrong: {},
  mathHintHidden: {},
  mathReviewCount: 0,
  mathReviewInterval: 3,
  currentTextId: null,
  events: [],
};

export const ReadingText = z.object({
  id: z.string(),
  text: z.string(),
  level: z.number().int().min(MIN_LEVEL).max(MAX_LEVEL),
  kind: ReadingKind,
});
export type ReadingText = z.infer<typeof ReadingText>;

export const ReadingEvent = z.object({
  id: z.string(),
  textId: z.string(),
  text: z.string(),
  level: z.number().int(),
  result: ResultKind,
  at: z.string(),
});
export type ReadingEvent = z.infer<typeof ReadingEvent>;

export const TextStat = z.object({
  textId: z.string(),
  correct: z.number().int().nonnegative(),
  wrong: z.number().int().nonnegative(),
  skip: z.number().int().nonnegative(),
  wayTooEasy: z.number().int().nonnegative(),
  cooldown: z.number().int().nonnegative(),
  retired: z.boolean(),
  lastPictureAt: z.string().optional(),
  pictureRefreshLeft: z.number().int().nonnegative().optional(),
});
export type TextStat = z.infer<typeof TextStat>;

export const Profile = z.object({
  id: z.string(),
  name: z.string().min(1),
  colorPairIndex: z.number().int().nonnegative(),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  band: ReadingBand,
  level: z.number().int().min(MIN_LEVEL).max(MAX_LEVEL),
  manuallySetLevel: z.boolean().default(false),
  createdAt: z.string(),
  currentTextId: z.string().nullable(),
  lastTextId: z.string().nullable(),
  recentTextIds: z.array(z.string()),
  boostActive: z.boolean(),
  boostLevel: z.number().int().min(MIN_LEVEL).max(MAX_LEVEL),
  correctStreak: z.number().int().nonnegative(),
  wrongStreak: z.number().int().nonnegative(),
  textStats: z.record(z.string(), TextStat),
  events: z.array(ReadingEvent),
  coins: z.number().int().nonnegative().default(0),
  coinsEarned: z.number().int().nonnegative().default(0),
  peakCoins: z.number().int().nonnegative().default(0),
  inventory: z.array(z.string()).default([]),
  itemPositions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  correctsUntilCoin: z.number().nonnegative().multipleOf(0.5).default(0),
  coinAwardsUntilBonus: z.number().int().nonnegative().default(0),
  maxCorrectStreak: z.number().int().nonnegative().default(0),
  achievements: z.array(z.string()).default([]),
  unlockedLocations: z.array(z.string()).default(["start"]),
  currentLocationId: z.string().default("start"),
  selectedSubject: Subject.optional(),
  spellingWordId: z.string().optional(),
  subjectProgress: z
    .object({
      reading: SubjectProgress,
      math: SubjectProgress,
      spelling: SubjectProgress.default(emptySubjectProgress),
    })
    .optional(),
});
export type Profile = z.infer<typeof Profile>;

export const IncomingShare = z.object({
  ownerId: z.string().min(1),
  profileId: z.string().min(1),
  shareId: z.string().uuid().optional(),
  permission: z.enum(["view", "contribute"]).optional(),
  ownerDisplayName: z.string().nullable().optional(),
});
export type IncomingShare = z.infer<typeof IncomingShare>;

export const AppState = z.object({
  version: z.literal(APP_VERSION),
  passcode: z.string().nullable(),
  currentProfileId: z.string().nullable(),
  profiles: z.array(Profile),
  incomingShares: z.array(IncomingShare).default([]),
});
export type AppState = z.infer<typeof AppState>;

export const emptyAppState = (): AppState => ({
  version: APP_VERSION,
  passcode: null,
  currentProfileId: null,
  profiles: [],
  incomingShares: [],
});

export const emptyTextStat = (textId: string): TextStat => ({
  textId,
  correct: 0,
  wrong: 0,
  skip: 0,
  wayTooEasy: 0,
  cooldown: 0,
  retired: false,
});
